/**
 * History パネル — landings/{id}/history を表示。
 * v1.0 は閲覧のみ。復元は v1.1 で実装。
 */
import type { LandingHistoryEntry } from '@goenhub/shared';
import { useQuery } from '@tanstack/react-query';
import { collection, getDocs, orderBy, query as fsQuery, limit } from 'firebase/firestore';
import { Sparkles, RefreshCw, Globe, Pencil } from 'lucide-react';

import { Spinner } from '@/components/ui/spinner';
import { db } from '@/lib/firebase';

interface HistoryRecord extends LandingHistoryEntry {
  id: string;
}

interface Props {
  landingId: string;
}

const KIND_LABELS: Record<LandingHistoryEntry['kind'], { label: string; icon: typeof Sparkles }> = {
  generate: { label: 'ページ生成', icon: Sparkles },
  regenerate_all: { label: '全体再生成', icon: RefreshCw },
  regenerate_section: { label: 'セクション再生成', icon: RefreshCw },
  regenerate_field: { label: 'フィールド再生成', icon: Pencil },
  publish: { label: '公開', icon: Globe },
};

function formatDate(d: unknown): string {
  if (!d) return '';
  if (typeof d === 'object' && d !== null && 'toDate' in d) {
    try {
      const date = (d as { toDate(): Date }).toDate();
      return date.toLocaleString('ja-JP', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '';
    }
  }
  return '';
}

export function HistoryPanel({ landingId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['landings', landingId, 'history'],
    queryFn: async (): Promise<HistoryRecord[]> => {
      const snap = await getDocs(
        fsQuery(
          collection(db, 'landings', landingId, 'history'),
          orderBy('createdAt', 'desc'),
          limit(50),
        ),
      );
      return snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as LandingHistoryEntry),
      }));
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="text-xs text-[var(--text-muted)] text-center py-4">まだ履歴がありません</div>
    );
  }

  return (
    <div className="space-y-1.5">
      {data.map((entry) => {
        const meta = KIND_LABELS[entry.kind] ?? { label: entry.kind, icon: Sparkles };
        const Icon = meta.icon;
        return (
          <div
            key={entry.id}
            className="border border-[var(--border)] rounded-md p-2 bg-[var(--bg-elevated)]"
          >
            <div className="flex items-center gap-2 mb-0.5">
              <Icon className="size-3 text-[var(--text-muted)]" />
              <span className="text-xs font-medium">{meta.label}</span>
              <span className="ml-auto text-[10px] text-[var(--text-muted)]">
                {formatDate(entry.createdAt)}
              </span>
            </div>
            {entry.targetSectionId && (
              <div className="text-[10px] text-[var(--text-muted)]">
                セクション: {entry.targetSectionId}
                {entry.targetFieldPath && ` / ${entry.targetFieldPath}`}
              </div>
            )}
            {entry.snapshot &&
              (entry.snapshot as Record<string, unknown>).sectionCount !== undefined && (
                <div className="text-[10px] text-[var(--text-muted)]">
                  {(entry.snapshot as Record<string, unknown>).sectionCount as number}{' '}
                  セクション生成
                </div>
              )}
            {entry.referenceFiles && entry.referenceFiles.length > 0 && (
              <div
                className="text-[10px] text-[var(--text-muted)] truncate"
                title={entry.referenceFiles.join('\n')}
              >
                参考資料: {entry.referenceFiles.join('、')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
